import type { ModuleMigrationDef } from "@kubdee/sdk";
import type { CapabilityName } from "../capabilities";

// Issue #249 M7: typed constant — a typo becomes a compile error.
const CAPABILITIES = JSON.stringify(["messaging"] satisfies CapabilityName[]);

/**
 * Seed the `instagram-dm` connector type row.
 *
 * - Idempotent via `INSERT OR IGNORE`: re-running the migration after P7
 *   follow-ups won't overwrite operator edits to the row (e.g. scope
 *   changes, icon swaps).
 * - Primary key `id` is set equal to `slug` so the capability registry can
 *   register under the SAME string the webhook ingress route uses —
 *   `connector_instances.type_id` ends up matching the slug too, which keeps
 *   the adapter lookup uniform across webhook and `call()` paths.
 * - `oauth_config` carries Instagram Login 2024 endpoints (NOT the legacy FB
 *   Login for Instagram flow). Scheduler DO (`scheduler.do.ts →
 *   dispatchTokenRefresh`) reads `{ tokenUrl, clientId, clientSecret? }` so
 *   the field names match the FB seed.
 * - `webhook_config` records the signature header + algorithm the adapter
 *   expects. The `secretEnv` points at `FB_APP_SECRET` because Meta apps
 *   share ONE secret across the FB + IG surfaces; operators can override
 *   with `INSTAGRAM_APP_SECRET` if they rotate IG independently (the
 *   adapter's `webhook.ts#pickSecret` prefers the IG-specific env var if
 *   present).
 * - `notes` (inside webhook_config) flags the business-verification +
 *   IG Business/Creator account requirement — surfaced by the onboarding UI.
 * - `icon` is the canonical Lucide / brand icon key (`instagram`). Color is
 *   Instagram's brand pink (`#E4405F`).
 * - `created_at` / `updated_at` use a fixed seed timestamp — tenants-local
 *   time isn't available at seed time, and the column is audit-only.
 *
 * IMPORTANT: Meta migrated Instagram DM APIs to the standalone "Instagram
 * Login" flow in 2024; the Facebook Login for Instagram flow is being
 * sunsetted. Scope names in `oauth_config` use the new Instagram Login
 * scope set (`instagram_business_basic` etc.). See
 * https://developers.facebook.com/docs/instagram-platform/instagram-login
 * for the canonical scope names.
 */
export const migration: ModuleMigrationDef = {
  key: "004_instagram_dm_seed",
  version: "0.4.0",
  sql: `
INSERT OR IGNORE INTO \`connectors_types\` (
  id, slug, name, icon, icon_url, capabilities, oauth_config, webhook_config,
  is_active, created_at, updated_at
) VALUES (
  'instagram-dm',
  'instagram-dm',
  'Instagram Direct Messages',
  'instagram',
  NULL,
  '${CAPABILITIES}',
  '{"tokenUrl":"https://api.instagram.com/oauth/access_token","authorizeUrl":"https://www.instagram.com/oauth/authorize","longLivedTokenUrl":"https://graph.instagram.com/access_token","refreshTokenUrl":"https://graph.instagram.com/refresh_access_token","clientIdEnv":"INSTAGRAM_DM_CLIENT_ID","clientSecretEnv":"INSTAGRAM_DM_CLIENT_SECRET","scopes":["instagram_business_basic","instagram_business_manage_messages","instagram_business_content_publish"],"color":"#E4405F","notes":"Requires Instagram Business or Creator account + Meta app review for messaging permissions. Uses Instagram Login 2024 (standalone) — NOT Facebook Login for Instagram."}',
  '{"signatureHeader":"x-hub-signature-256","algorithm":"HMAC-SHA256","secretEnv":"INSTAGRAM_APP_SECRET","secretEnvFallback":"FB_APP_SECRET","events":["messages","messaging_reactions","message_reads","story_mentions","messaging_referral"]}',
  1,
  '2026-04-17T00:00:00Z',
  '2026-04-17T00:00:00Z'
);
  `.trim(),
};
